class ProductImageZoom extends HTMLElement {
  static FADE_DURATION = 200;
  static SLIDE_DURATION = 3000;

  static ARROW_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 10"><path fill="currentColor" fill-rule="evenodd" d="M8.537.808a.5.5 0 0 1 .817-.162l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 1 1-.708-.708L11.793 5.5H1a.5.5 0 0 1 0-1h10.793L8.646 1.354a.5.5 0 0 1-.109-.546" clip-rule="evenodd"/></svg>';

  static CLOSE_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 18 17"><path fill="currentColor" d="M.865 15.978a.5.5 0 0 0 .707.707l7.433-7.431 7.579 7.282a.501.501 0 0 0 .846-.37.5.5 0 0 0-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 1 0-.707-.708L8.991 7.853 1.413.573a.5.5 0 1 0-.693.72l7.563 7.268z"/></svg>';

  static PLAY_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"><path fill="currentColor" d="M8 5v14l11-7z"/></svg>';

  static PAUSE_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"><rect x="6" y="5" width="4" height="14" fill="currentColor"/><rect x="14" y="5" width="4" height="14" fill="currentColor"/></svg>';

  static EXPAND_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 3H5a2 2 0 0 0-2 2v3"/><path d="M21 8V5a2 2 0 0 0-2-2h-3"/><path d="M3 16v3a2 2 0 0 0 2 2h3"/><path d="M16 21h3a2 2 0 0 0 2-2v-3"/></svg>';

  static COMPRESS_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 3v3a2 2 0 0 1-2 2H3"/><path d="M21 8h-3a2 2 0 0 1-2-2V3"/><path d="M3 16h3a2 2 0 0 1 2 2v3"/><path d="M16 21v-3a2 2 0 0 1 2-2h3"/></svg>';

  static GRID_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="7" height="7" rx="1" fill="currentColor"/><rect x="14" y="3" width="7" height="7" rx="1" fill="currentColor"/><rect x="3" y="14" width="7" height="7" rx="1" fill="currentColor"/><rect x="14" y="14" width="7" height="7" rx="1" fill="currentColor"/></svg>';

  connectedCallback() {
    this.images = [];
    this.currentIndex = 0;
    this.isZoomed = false;

    this.buildModal();
    document.addEventListener('click', this.handleGalleryClick.bind(this), true);
    document.addEventListener('keydown', this.handleKeydown.bind(this));
  }

  handleGalleryClick(event) {
    const toggle = event.target.closest('.product__media-toggle');
    if (!toggle) return;

    const container = toggle.closest('.product-media-container');
    if (!container || !container.classList.contains('media-type-image')) return;

    const productInfo = toggle.closest('product-info');
    if (productInfo?.dataset.imageZoomEffect !== 'custom') return;

    event.preventDefault();
    event.stopImmediatePropagation();

    this.collectImages(container);
    this.open();
  }

  collectImages(clickedContainer) {
    const scope = clickedContainer.closest('.product__media-list') || document;
    const containers = Array.from(scope.querySelectorAll('.product-media-container.media-type-image'));

    this.images = containers.map((container) => {
      const img = container.querySelector('img');
      return {
        src: this.getHighResSrc(img),
        alt: img?.alt || '',
      };
    });

    this.currentIndex = Math.max(containers.indexOf(clickedContainer), 0);
  }

  getHighResSrc(img) {
    if (!img) return '';
    const src = img.currentSrc || img.src;
    if (!src) return '';
    if (/[?&]width=\d+/.test(src)) {
      return src.replace(/([?&])width=\d+/, `$1width=2048`);
    }
    return src;
  }

  buildModal() {
    this.modal = document.createElement('div');
    this.modal.className = 'product-zoom-modal';
    this.modal.setAttribute('role', 'dialog');
    this.modal.setAttribute('aria-modal', 'true');
    this.modal.setAttribute('aria-label', 'Product image zoom');

    this.modal.innerHTML = `
      <div class="product-zoom-modal__body">
        <div class="product-zoom-modal__main">
          <div class="product-zoom-modal__progress">
            <div class="product-zoom-modal__progress-bar"></div>
          </div>
          <div class="product-zoom-modal__toolbar">
            <button type="button" class="product-zoom-modal__toolbar-btn product-zoom-modal__play" aria-label="Play slideshow">
              ${ProductImageZoom.PLAY_SVG}
            </button>
            <button type="button" class="product-zoom-modal__toolbar-btn product-zoom-modal__fullscreen" aria-label="Toggle fullscreen">
              ${ProductImageZoom.EXPAND_SVG}
            </button>
            <button type="button" class="product-zoom-modal__toolbar-btn product-zoom-modal__grid-toggle" aria-label="Toggle thumbnail grid">
              ${ProductImageZoom.GRID_SVG}
            </button>
            <button type="button" class="product-zoom-modal__toolbar-btn product-zoom-modal__close" aria-label="Close">
              ${ProductImageZoom.CLOSE_SVG}
            </button>
          </div>
          <button type="button" class="product-zoom-modal__nav product-zoom-modal__nav--prev" aria-label="Previous image">
            ${ProductImageZoom.ARROW_SVG}
          </button>
          <div class="product-zoom-modal__stage">
            <img class="product-zoom-modal__image" src="" alt="" draggable="false">
          </div>
          <button type="button" class="product-zoom-modal__nav product-zoom-modal__nav--next" aria-label="Next image">
            ${ProductImageZoom.ARROW_SVG}
          </button>
        </div>
        <div class="product-zoom-modal__thumbnails"></div>
      </div>
    `;

    document.body.appendChild(this.modal);

    this.stageEl = this.modal.querySelector('.product-zoom-modal__stage');
    this.imageEl = this.modal.querySelector('.product-zoom-modal__image');
    this.prevButton = this.modal.querySelector('.product-zoom-modal__nav--prev');
    this.nextButton = this.modal.querySelector('.product-zoom-modal__nav--next');
    this.thumbnailsEl = this.modal.querySelector('.product-zoom-modal__thumbnails');
    this.progressBarEl = this.modal.querySelector('.product-zoom-modal__progress-bar');
    this.playButton = this.modal.querySelector('.product-zoom-modal__play');
    this.fullscreenButton = this.modal.querySelector('.product-zoom-modal__fullscreen');
    this.gridToggleButton = this.modal.querySelector('.product-zoom-modal__grid-toggle');

    this.isPlaying = false;
    this.isGridHidden = false;

    this.modal.querySelector('.product-zoom-modal__close').addEventListener('click', () => this.close());
    this.prevButton.addEventListener('click', () => {
      this.stopSlideshow();
      this.navigate(-1);
    });
    this.nextButton.addEventListener('click', () => {
      this.stopSlideshow();
      this.navigate(1);
    });

    this.playButton.addEventListener('click', () => this.togglePlay());
    this.fullscreenButton.addEventListener('click', () => this.toggleFullscreen());
    this.gridToggleButton.addEventListener('click', () => this.toggleGrid());

    document.addEventListener('fullscreenchange', () => this.updateFullscreenIcon());

    const backdropTargets = () => [this.modal, this.modal.querySelector('.product-zoom-modal__body'), this.modal.querySelector('.product-zoom-modal__main'), this.stageEl];
    this.modal.addEventListener('click', (event) => {
      if (backdropTargets().includes(event.target)) this.close();
    });

    this.imageEl.addEventListener('click', (event) => {
      event.stopPropagation();
      this.toggleZoom(event);
    });

    this.imageEl.addEventListener('mousemove', (event) => {
      if (this.isZoomed) this.updateZoomOrigin(event);
    });
  }

  handleKeydown(event) {
    if (!this.modal.classList.contains('is-open')) return;
    if (event.key === 'Escape') this.close();
    if (event.key === 'ArrowLeft') {
      this.stopSlideshow();
      this.navigate(-1);
    }
    if (event.key === 'ArrowRight') {
      this.stopSlideshow();
      this.navigate(1);
    }
  }

  toggleZoom(event) {
    this.isZoomed = !this.isZoomed;
    this.imageEl.classList.toggle('is-zoomed', this.isZoomed);
    if (this.isZoomed) {
      this.updateZoomOrigin(event);
    } else {
      this.imageEl.style.transformOrigin = 'center center';
    }
  }

  updateZoomOrigin(event) {
    const rect = this.imageEl.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    this.imageEl.style.transformOrigin = `${x}% ${y}%`;
  }

  open() {
    this.isGridHidden = false;
    this.thumbnailsEl.classList.remove('is-hidden');
    this.gridToggleButton.classList.add('is-active');
    this.renderThumbnails();
    this.render();
    this.modal.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.modal.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden');
    this.isZoomed = false;
    this.isTransitioning = false;
    this.imageEl.classList.remove('is-zoomed', 'is-fading');
    this.imageEl.style.transformOrigin = 'center center';
    this.stopSlideshow();
    if (document.fullscreenElement === this.modal) document.exitFullscreen();
  }

  navigate(direction) {
    if (this.images.length < 2) return;
    const targetIndex = (this.currentIndex + direction + this.images.length) % this.images.length;
    this.goToIndex(targetIndex);
  }

  goToIndex(index) {
    if (index === this.currentIndex || this.isTransitioning || !this.images[index]) return;

    this.isTransitioning = true;
    this.isZoomed = false;
    this.imageEl.classList.remove('is-zoomed');
    this.imageEl.style.transformOrigin = 'center center';
    this.imageEl.classList.add('is-fading');

    window.setTimeout(() => {
      this.currentIndex = index;
      this.render();
      this.imageEl.classList.remove('is-fading');
      this.isTransitioning = false;
    }, ProductImageZoom.FADE_DURATION);
  }

  togglePlay() {
    if (this.isPlaying) {
      this.stopSlideshow();
    } else {
      this.startSlideshow();
    }
  }

  startSlideshow() {
    if (this.images.length < 2) return;
    this.isPlaying = true;
    this.playButton.classList.add('is-playing');
    this.playButton.innerHTML = ProductImageZoom.PAUSE_SVG;
    this.playButton.setAttribute('aria-label', 'Pause slideshow');
    this.runSlideshowStep();
  }

  stopSlideshow() {
    if (!this.isPlaying) return;
    this.isPlaying = false;
    window.clearTimeout(this.slideshowTimer);
    this.playButton.classList.remove('is-playing');
    this.playButton.innerHTML = ProductImageZoom.PLAY_SVG;
    this.playButton.setAttribute('aria-label', 'Play slideshow');
    this.resetProgressBar();
  }

  runSlideshowStep() {
    this.resetProgressBar();

    requestAnimationFrame(() => {
      this.progressBarEl.style.transition = `width ${ProductImageZoom.SLIDE_DURATION}ms linear`;
      this.progressBarEl.style.width = '100%';
    });

    this.slideshowTimer = window.setTimeout(() => {
      const targetIndex = (this.currentIndex + 1) % this.images.length;
      this.goToIndex(targetIndex);
      if (this.isPlaying) this.runSlideshowStep();
    }, ProductImageZoom.SLIDE_DURATION);
  }

  resetProgressBar() {
    this.progressBarEl.style.transition = 'none';
    this.progressBarEl.style.width = '0%';
  }

  toggleFullscreen() {
    if (document.fullscreenElement === this.modal) {
      document.exitFullscreen();
    } else if (this.modal.requestFullscreen) {
      this.modal.requestFullscreen();
    }
  }

  updateFullscreenIcon() {
    const isFullscreen = document.fullscreenElement === this.modal;
    this.fullscreenButton.innerHTML = isFullscreen ? ProductImageZoom.COMPRESS_SVG : ProductImageZoom.EXPAND_SVG;
    this.fullscreenButton.setAttribute('aria-label', isFullscreen ? 'Exit fullscreen' : 'Toggle fullscreen');
  }

  toggleGrid() {
    this.isGridHidden = !this.isGridHidden;
    this.thumbnailsEl.classList.toggle('is-hidden', this.isGridHidden);
    this.gridToggleButton.classList.toggle('is-active', !this.isGridHidden);
  }

  renderThumbnails() {
    this.thumbnailsEl.innerHTML = '';

    if (this.images.length < 2) return;

    this.images.forEach((image, index) => {
      const thumbnail = document.createElement('button');
      thumbnail.type = 'button';
      thumbnail.className = 'product-zoom-modal__thumbnail';
      thumbnail.setAttribute('aria-label', `Show image ${index + 1}`);
      thumbnail.innerHTML = `<img src="${image.src}" alt="${image.alt}" draggable="false">`;
      thumbnail.addEventListener('click', () => {
        this.stopSlideshow();
        this.goToIndex(index);
      });
      this.thumbnailsEl.appendChild(thumbnail);
    });
  }

  render() {
    const current = this.images[this.currentIndex];
    if (!current) return;
    this.imageEl.src = current.src;
    this.imageEl.alt = current.alt;

    const hasMultiple = this.images.length > 1;
    this.prevButton.hidden = !hasMultiple;
    this.nextButton.hidden = !hasMultiple;

    let activeThumbnail = null;
    this.thumbnailsEl.querySelectorAll('.product-zoom-modal__thumbnail').forEach((thumbnail, index) => {
      const isActive = index === this.currentIndex;
      thumbnail.classList.toggle('is-active', isActive);
      if (isActive) activeThumbnail = thumbnail;
    });

    activeThumbnail?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }
}

if (!customElements.get('product-image-zoom')) {
  customElements.define('product-image-zoom', ProductImageZoom);
}

document.addEventListener('DOMContentLoaded', () => {
  if (!document.querySelector('product-image-zoom')) {
    document.body.appendChild(document.createElement('product-image-zoom'));
  }
});
